<template>
  <modal
    :show.sync="showModal"
    modal-classes="modal-dialog-centered modal-lg "
    @close="closeModal()"
    no-close-on-backdrop
  >
    <div class="review modal-availability">
      <div class="text-center border-bottom mb-2">
        <img
          src="~rhomtrip_assets/src/img/icons/alerts/warning.png"
          alt="warning"
          class="icon-modal-alert"
        />
        <h2 class="modal-title">Pending Changes</h2>
        <p>
          These changes were requested but the payment of the price difference
          was not completed, so they have not been applied. The original
          reservation stays active.
        </p>
      </div>

      <div class="card mb-3" v-if="rows.length">
        <div class="col-12">
          <div class="row">
            <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
              <h4 class="subtitle mb-1 mt-1">Original Details</h4>
              <div
                class="item-list"
                v-for="row in rows"
                :key="`original-${row.key}`"
              >
                <span class="data-cancel">
                  <strong>{{ row.label }}</strong> {{ displayValue(row.from) }}
                </span>
              </div>
            </div>
            <div class="col-xl-6 col-lg-6 col-md-12 col-sm-12 col-12">
              <h4 class="subtitle mb-1 mt-1">Requested Changes</h4>
              <div
                class="item-list"
                v-for="row in rows"
                :key="`requested-${row.key}`"
              >
                <span>
                  <strong>{{ row.label }}</strong> {{ displayValue(row.to) }}
                  <div class="badge badge-changed">Changed</div>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <table cellspacing="0" cellpadding="1" class="table-prices mb-3">
        <tbody>
          <tr>
            <td align="left"><span class="price">Original Total</span></td>
            <td align="right">
              <ServicePrice
                :price="pendingChange.previousTotal || 0"
                :currencyCode="currencyCode"
                :currencySymbol="currencySymbol"
                :currency="currency"
                nameClass="price"
              />
            </td>
          </tr>
          <tr>
            <td align="left"><span class="price">New Total</span></td>
            <td align="right">
              <ServicePrice
                :price="pendingChange.newTotal || 0"
                :currencyCode="currencyCode"
                :currencySymbol="currencySymbol"
                :currency="currency"
                nameClass="price"
              />
            </td>
          </tr>
          <tr class="footer-table">
            <td align="left"><span class="totals">Difference to Pay</span></td>
            <td align="right">
              <ServicePrice
                :price="pendingChange.priceDifference || 0"
                :currencyCode="currencyCode"
                :currencySymbol="currencySymbol"
                :currency="currency"
                nameClass="totals"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <p class="f-12 mb-0">
        Requested on {{ formatDateTime(pendingChange.created_at) }}
      </p>
    </div>

    <template slot="footer">
      <base-button
        type="outline-neutral"
        class="min-button mb-0"
        @click="closeModal()"
        >Close</base-button
      >

      <base-button
        type="danger"
        class="min-button ml-auto mb-0"
        :disabled="cancelling"
        @click="confirmCancel()"
      >
        {{ cancelling ? "Cancelling..." : "Cancel Request" }}
      </base-button>
    </template>
  </modal>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters, mapMutations } from "vuex";
import ServicePrice from "@/components/ServicePrice.vue";
import { ReservationsAPI } from "@/core/api";
import { isUnauthorizedError } from "@/helpers/commons.helpers";
import { cancelPendingChange } from "@/helpers/reservation.helpers";
import { formatPickupDate } from "../services/format.service";
import { toTransferDetails } from "../services/changes.service";

// Same fields (and labels) the ConfirmChanges modal compares when the change
// is requested, so both show the same diff. Only these are shown to the
// partner; total charges go in the totals table (gross only).
const COMPARISON_FIELDS = [
  { key: "from", label: "From" },
  { key: "to", label: "To:" },
  { key: "dateTime", label: "Date & Time:" },
  { key: "paxCount", label: "PAX N°:" },
  { key: "flightNumber", label: "Flight Number:" },
  { key: "railLine", label: "Rail Line:" },
  { key: "cruiseShip", label: "Cruise Ship:" },
  { key: "hourly", label: "Hourly:" },
  { key: "serviceType", label: "Service Type:" },
  { key: "vehicle", label: "Vehicle:" },
  { key: "paxName", label: "PAX:" },
  { key: "email", label: "Email:" },
  { key: "contactNumber", label: "Contact Number:" },
  { key: "pickupSign", label: "Pickup Sign:" },
  { key: "specialRequirements", label: "Special Requirements:" },
  { key: "childSeatsCount", label: "Child Seats N°:" },
  { key: "bookingReference", label: "Reservation Reference:" }
];

export default {
  name: "PendingChangeModal",
  components: { ServicePrice },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    reservationId: {
      type: String,
      required: true
    },
    pendingChange: {
      type: Object,
      required: true
    },
    originalReservation: {
      type: Object,
      required: true,
      description: "Reservation as currently saved (the change is not applied yet)"
    },
    originalService: {
      type: Object,
      default: null,
      description: "Populated service of the saved reservation"
    },
    currency: {
      type: Object,
      default: () => ({ code: "", symbol: "", rates: null }),
      description: "Display currency for ServicePrice"
    },
    currencyCode: {
      type: String,
      default: ""
    },
    currencySymbol: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      showModal: this.show,
      cancelling: false
    };
  },
  computed: {
    ...mapGetters(["sesion"]),
    originalDetails() {
      return toTransferDetails(this.originalReservation, this.originalService);
    },
    requestedDetails() {
      const requested = this.pendingChange.pendingPayload;
      if (!requested) return this.originalDetails;
      return toTransferDetails(
        { ...requested, pickup_date: formatPickupDate(requested.pickup_date) },
        this.requestedService
      );
    },
    requestedService() {
      const vehicle = this.pendingChange.requestedVehicle;
      if (!vehicle) return this.originalService;
      return {
        vehicle: {
          category: vehicle.category,
          seats: vehicle.seats,
          make_label: vehicle.name
        }
      };
    },
    rows() {
      const rows = COMPARISON_FIELDS.map(field => ({
        ...field,
        from: this.originalDetails[field.key],
        to: this.requestedDetails[field.key]
      })).filter(row => row.from !== row.to);
      const { previousSupplier, requestedSupplier } = this.pendingChange;
      if (requestedSupplier) {
        rows.push({
          key: "supplier",
          label: "Supplier:",
          from: previousSupplier && previousSupplier.name,
          to: requestedSupplier.name
        });
      }
      return rows;
    }
  },
  watch: {
    show(current) {
      this.showModal = current;
    }
  },
  methods: {
    ...mapMutations(["setSigOut"]),
    displayValue(value) {
      return value === null || value === undefined || value === "" ? "-" : value;
    },
    formatDateTime(date) {
      return date ? moment(date).format("MMM DD, YYYY HH:mm") : "";
    },
    closeModal() {
      this.showModal = false;
      this.$emit("close");
    },
    async confirmCancel() {
      await cancelPendingChange(this.submitCancelPendingChange);
    },
    async submitCancelPendingChange(reason) {
      this.cancelling = true;
      try {
        await ReservationsAPI.cancelPendingChangeByReservationId(
          this.reservationId,
          reason,
          this.sesion.token
        );
        this.$notify({
          type: "success",
          title: "Success!",
          message: "The pending changes have been cancelled",
          icon: "flaticon-check-3"
        });
        this.$emit("cancelled");
        this.closeModal();
      } catch (error) {
        if (isUnauthorizedError(error)) {
          this.setSigOut(true);
          return;
        }
        const status = error.response && error.response.status;
        this.$notify({
          type: "danger",
          title: "Error!",
          message:
            status === 409
              ? "These changes can no longer be cancelled. The payment may have just been completed, please refresh the reservation."
              : "The pending changes could not be cancelled. Please try again",
          icon: "flaticon-remove"
        });
      } finally {
        this.cancelling = false;
      }
    }
  }
};
</script>
